"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import Swal from "sweetalert2"
import { useAuth } from "../../../contexts/AuthContext"
import { API_BASE_URL as API_BASE } from "../../../config/api"
import { getAuthToken, getAuthUser } from "../../../utils/authStorage"
import { checkCampaignCountryPermissions } from "../../../utils/countryValidation"

function SingleCampaign() {
  const navigate = useNavigate()
  const { currentUser, user } = useAuth()
  console.log("Auth context data:", { currentUser, user })

  // Fallback: Try to get user from localStorage if context is empty
  const fallbackUser = user || currentUser || getAuthUser() || {}
  console.log("Final user data:", fallbackUser)
  const effectiveRole = fallbackUser?.role
  const isRestrictedRole = ["superadmin", "admin", "reseller", "company"].includes(effectiveRole)

  useEffect(() => {
    if (isRestrictedRole) {
      navigate(effectiveRole === "reseller" ? "/dashreseller" : (effectiveRole === "superadmin" ? "/su-admin" : "/dash-admin"), { replace: true })
    }
  }, [isRestrictedRole, effectiveRole, navigate])

  const [loading, setLoading] = useState(true)
  const [templates, setTemplates] = useState([])
  const [filteredTemplates, setFilteredTemplates] = useState([])
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [campaignName, setCampaignName] = useState("")
  const [phoneNumbers, setPhoneNumbers] = useState("")
  const [recipientCount, setRecipientCount] = useState(0)
  const [manualVariables, setManualVariables] = useState({})
  const [uploadedMedia, setUploadedMedia] = useState(null)
  const [savedVariables, setSavedVariables] = useState({})
  const [useSavedVariables, setUseSavedVariables] = useState(true)
  const [userBalance, setUserBalance] = useState(0)
  const [balanceValidation, setBalanceValidation] = useState(null)
  const [canProceed, setCanProceed] = useState(false)
  const [campaignId, setCampaignId] = useState(null)
  const [isCreating, setIsCreating] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [campaignSent, setCampaignSent] = useState(false)
  const [phoneInputMethod, setPhoneInputMethod] = useState("manual")
  const [inputMethod, setInputMethod] = useState("manual")
  const [excelData, setExcelData] = useState([])
  const [templateSearchQuery, setTemplateSearchQuery] = useState("")
  const [wabaAccounts, setWabaAccounts] = useState([])
  const [selectedWabaAccount, setSelectedWabaAccount] = useState(null)
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState(null)
  const [variableMapping, setVariableMapping] = useState({})
  const [excelColumns, setExcelColumns] = useState([])
  const [showColumnMapping, setShowColumnMapping] = useState(false)
  const [uploadedVariableFile, setUploadedVariableFile] = useState(null)
  const [phoneColumn, setPhoneColumn] = useState("")
  const [totalRecords, setTotalRecords] = useState(0)
  const [excelMappingConfigured, setExcelMappingConfigured] = useState(false)
  const [campaignCreated, setCampaignCreated] = useState(false)

  useEffect(() => {
    loadTemplates()
    loadUserBalance()
    loadWabaAccounts()
  }, [])

  useEffect(() => {
    if (selectedWabaAccount && selectedWabaAccount.phoneNumbers && selectedWabaAccount.phoneNumbers.length > 0) {
      if (user?.operator === "pinacle") {
        const defaultPhone = selectedWabaAccount.phoneNumbers.find((p) => p.default === true)
        if (defaultPhone) {
          setSelectedPhoneNumber(defaultPhone)
        } else if (!selectedPhoneNumber) {
          setSelectedPhoneNumber(selectedWabaAccount.phoneNumbers[0])
        }
      } else if (!selectedPhoneNumber) {
        setSelectedPhoneNumber(selectedWabaAccount.phoneNumbers[0])
      }
    }
  }, [selectedWabaAccount, user?.operator])

  useEffect(() => {
    if (phoneNumbers) {
      const numbers = phoneNumbers.split("\n").filter((num) => num.trim().length > 0)
      setRecipientCount(numbers.length)
    } else {
      setRecipientCount(0)
    }
  }, [phoneNumbers])

  useEffect(() => {
    validateBalance()
  }, [recipientCount, userBalance])

  useEffect(() => {
    if (templateSearchQuery.trim() === "") {
      setFilteredTemplates(templates)
    } else {
      const filtered = templates.filter(
        (template) =>
          template.name.toLowerCase().includes(templateSearchQuery.toLowerCase()) ||
          template.category.toLowerCase().includes(templateSearchQuery.toLowerCase()),
      )
      setFilteredTemplates(filtered)
    }
  }, [templateSearchQuery, templates])

  useEffect(() => {
    // Save variables to database when template has filled variables
    if (selectedTemplate && selectedTemplate.parameterAnalysis?.bodyParameterCount > 0) {
      const hasValues = Object.values(manualVariables).some(val => val && val.trim() !== "")
      if (hasValues) {
        // Debounce save to avoid too many API calls
        const timeoutId = setTimeout(() => {
          saveTemplateVariables(selectedTemplate.id, manualVariables)
        }, 1000)

        return () => clearTimeout(timeoutId)
      }
    }
  }, [selectedTemplate, manualVariables])

  useEffect(() => {
    // Load saved variables from database when template changes
    if (selectedTemplate && useSavedVariables) {
      console.log("Loading variables for template:", selectedTemplate.id)
      loadTemplateVariables(selectedTemplate.id).then(saved => {
        if (saved && selectedTemplate.parameterAnalysis?.bodyParameterCount > 0) {
          setManualVariables(saved)
          console.log("Loaded saved variables for template:", selectedTemplate.name, saved)
        } else {
          // Clear variables if no saved data
          setManualVariables({})
        }
      }).catch(error => {
        console.error("Failed to load template variables:", error)
        setManualVariables({})
      })
    }
  }, [selectedTemplate, useSavedVariables])

  useEffect(() => {
    // Auto-select phone number when WABA account changes (only for cloud operator)
    if (fallbackUser?.operator === "cloud" && selectedWabaAccount && selectedWabaAccount.phoneNumbers) {
      console.log("WABA account phone numbers:", selectedWabaAccount.phoneNumbers)

      if (selectedWabaAccount.phoneNumbers.length === 1) {
        // Auto-select if only one phone number
        setSelectedPhoneNumber(selectedWabaAccount.phoneNumbers[0])
        console.log("Auto-selected single phone number:", selectedWabaAccount.phoneNumbers[0])
      } else if (selectedWabaAccount.phoneNumbers.length > 1) {
        // Auto-select default phone number if available
        const defaultPhone = selectedWabaAccount.phoneNumbers.find(p => p.default)
        if (defaultPhone) {
          setSelectedPhoneNumber(defaultPhone)
          console.log("Auto-selected default phone number:", defaultPhone)
        }
      }
    } else if (fallbackUser?.operator !== "cloud") {
      // For non-cloud operators, set phone number as not required
      setSelectedPhoneNumber({ id: "not-required", phoneNumber: "Not Required" })
      console.log("Phone number not required for operator:", fallbackUser?.operator)
    }
  }, [selectedWabaAccount, fallbackUser?.operator])

  useEffect(() => {
    // Re-validate balance when campaign is created or recipient count changes
    if (campaignCreated || recipientCount > 0) {
      validateBalance()
    }
  }, [campaignCreated, recipientCount, userBalance])

  useEffect(() => {
    // Auto-create campaign when campaign name is entered and template is selected
    if (selectedTemplate && campaignName.trim().length >= 3 && !campaignCreated && !isCreating) {
      console.log("Auto-creating campaign...")
      createCampaign()
    }
  }, [selectedTemplate, campaignName, campaignCreated])


  const saveTemplateVariables = async (templateId, variables) => {
    try {
      console.log("Saving variables for template:", templateId, variables)
      const response = await fetch(`${API_BASE}/api/templateVariables/templates/${templateId}/variables`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ variables }),
      })

      console.log("Save response status:", response.status)
      if (response.ok) {
        const data = await response.json()
        console.log("Variables saved successfully:", data)
        return true
      } else {
        const errorData = await response.json()
        console.error("Failed to save variables:", response.status, errorData)
        return false
      }
    } catch (error) {
      console.error("Error saving template variables:", error)
      return false
    }
  }

  const loadTemplateVariables = async (templateId) => {
    try {
      console.log("Loading variables for template:", templateId)
      const response = await fetch(`${API_BASE}/api/templateVariables/templates/${templateId}/variables`, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      })

      console.log("Load response status:", response.status)
      if (response.ok) {
        const data = await response.json()
        console.log("Variables loaded successfully:", data)
        return data.variables || {}
      } else if (response.status === 404) {
        console.log("No saved variables found for template")
        return null
      } else {
        const errorData = await response.json()
        console.error("Failed to load variables:", response.status, errorData)
        return null
      }
    } catch (error) {
      console.error("Error loading template variables:", error)
      return null
    }
  }

  const clearAllFields = () => {
    console.log("Clearing all campaign fields...")
    setSelectedTemplate(null)
    setCampaignName("")
    setUploadedMedia(null)
    setManualVariables({})
    setPhoneNumbers("")
    setRecipientCount(0)
    setCampaignId(null)
    setCampaignCreated(false)
    setCampaignSent(false)
    setBalanceValidation(null)
    setCanProceed(false)
    setVariableMapping({})
    setExcelColumns([])
    setShowColumnMapping(false)
    setUploadedVariableFile(null)
    setPhoneColumn("")
    setTotalRecords(0)
    setExcelMappingConfigured(false)
    setTemplateSearchQuery("")
    console.log("All fields cleared")
  }

  const getMediaType = () => {
    // First check components structure (more reliable)
    const headerComponent = selectedTemplate?.components?.find(c => c.type === "HEADER")
    if (headerComponent?.format) {
      return headerComponent.format.toLowerCase()
    }
    // Fallback to parameterAnalysis
    if (selectedTemplate?.parameterAnalysis?.headerType) {
      return selectedTemplate.parameterAnalysis.headerType.toLowerCase()
    }
    return null
  }

  const loadWabaAccounts = async () => {
    try {
      console.log("Loading WABA accounts...")
      const response = await fetch(`${API_BASE}/api/waba/accounts`, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        console.log("WABA accounts response:", data)
        setWabaAccounts(data.accounts || [])

        if (data.accounts && data.accounts.length === 1) {
          setSelectedWabaAccount(data.accounts[0])
          console.log("Auto-selected WABA account:", data.accounts[0])
        } else if (data.accounts && data.accounts.length > 1) {
          setSelectedWabaAccount(data.accounts[0])
          console.log("Auto-selected first WABA account:", data.accounts[0])
        }
      } else {
        console.log("Failed to load WABA accounts:", response.status)
      }
    } catch (error) {
      console.error("Error loading WABA accounts:", error)
    }
  }

  const loadTemplates = async () => {
    try {
      console.log("Loading templates...")
      const response = await fetch(`${API_BASE}/api/campaigns/templates`, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      })
      const data = await response.json()
      console.log("Templates response:", data)
      if (data.success && data.templates) {
        console.log("Setting templates:", data.templates)
        setTemplates(data.templates)
        setFilteredTemplates(data.templates)
      } else {
        console.log("No templates found or success false")
      }
    } catch (error) {
      console.error("Error loading templates:", error)
    } finally {
      setLoading(false)
    }
  }

  const loadUserBalance = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/user/balance`, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        const data = await response.json()
        setUserBalance(data.balance || 0)
      }
    } catch (error) {
      console.error("Error loading user balance:", error)
    }
  }

  const validateBalance = async () => {
    console.log("Validating balance:", { recipientCount, userBalance, campaignCreated })

    if (recipientCount === 0) {
      setBalanceValidation(null)
      setCanProceed(false)
      return
    }

    const costPerMessage = 0.25
    const totalCost = recipientCount * costPerMessage
    console.log("Cost calculation:", { recipientCount, costPerMessage, totalCost, userBalance })

    if (userBalance >= totalCost) {
      setBalanceValidation({
        status: "success",
        message: `✓ Sufficient balance. `,
        totalCost,
      })
      setCanProceed(true)
    } else {
      setBalanceValidation({
        status: "success",
        message: `✓ Message Send Successfully!`,
        totalCost,
      })
      setCanProceed(false)
    }
  }

  const createCampaign = async () => {
    if (campaignId && campaignId !== "null" && campaignId !== null) {
      console.log("Campaign already exists with ID:", campaignId)
      setCampaignCreated(true)
      return campaignId
    }

    const analysis = selectedTemplate?.parameterAnalysis || {}

    let bodyVariables = []

    if (inputMethod === "manual") {
      bodyVariables = Object.keys(manualVariables)
        .filter((key) => manualVariables[key] && manualVariables[key].trim() !== "")
        .map((key) => ({
          index: Number.parseInt(key),
          name: `Variable ${Number.parseInt(key) + 1}`,
          type: "text",
          defaultValue: manualVariables[key].trim(),
        }))
    } else if (inputMethod === "excel") {
      bodyVariables = []
    }

    let headerContentData = uploadedMedia
    const mediaType = analysis.headerType || getMediaType()

    if (user?.operator === "karix" && uploadedMedia && mediaType) {
      headerContentData = {
        type: mediaType.toLowerCase(),
        url: uploadedMedia.mediaUrl || uploadedMedia.whatsappUrl,
        ...(mediaType.toLowerCase() === "document" && uploadedMedia.fileName
          ? { fileName: uploadedMedia.fileName }
          : {}),
      }
    }

    const templateCategory = (selectedTemplate.category || "").toString().trim().toLowerCase()
    const validMessageTypes = ["marketing", "utility", "authentication"]
    const campaignMessageType = validMessageTypes.includes(templateCategory) ? templateCategory : "marketing"

    const campaignData = {
      campaignName: campaignName.trim(),
      templateId: selectedTemplate.id,
      templateName: selectedTemplate.name,
      templateData: selectedTemplate,
      language: selectedTemplate.language || "en",
      headerType: analysis.headerType || (getMediaType() ? getMediaType().toUpperCase() : undefined),
      headerContent: headerContentData,
      bodyVariables,
      mediaId: ["cloud", "pinacle"].includes(fallbackUser?.operator) ? uploadedMedia?.mediaId : null,
      mediaUrl: uploadedMedia?.mediaUrl || uploadedMedia?.whatsappUrl || null,
      messageType: campaignMessageType,
      wabaAccountId: selectedWabaAccount?.id,
      inputMethod,
      karixSenderFrom: undefined,
      karixWebhookDNId: undefined,
    }

    try {
      setIsCreating(true)
      const response = await fetch(`${API_BASE}/api/campaigns/create`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(campaignData),
      })

      const data = await response.json()
      console.log("Campaign creation response:", data)

      if (response.ok && data) {
        const newCampaignId = data.campaign?._id || data._id || data.id || data.campaignId || null

        if (newCampaignId && newCampaignId !== "null") {
          console.log("Setting campaign ID:", newCampaignId)
          setCampaignId(newCampaignId)
          setCampaignCreated(true)
          return newCampaignId
        } else {
          console.error("No valid campaign ID in response:", data)
          throw new Error("Failed to create campaign - no campaign ID returned")
        }
      } else {
        throw new Error(data.error || data.message || "Failed to create campaign")
      }
    } catch (error) {
      console.error("Campaign creation error:", error)
      alert(`Error creating campaign: ${error.message}`)
      return false
    } finally {
      setIsCreating(false)
    }
  }

  const handleMediaUpload = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    const formData = new FormData()
    formData.append("media", file)

    try {
      const response = await fetch(`${API_BASE}/api/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: formData,
      })

      const data = await response.json()

      if (data.success) {
        setUploadedMedia({
          mediaId: data.data.mediaId,
          whatsappUrl: data.data.whatsappUrl || data.data.mediaUrl || "",
          mediaUrl: data.data.mediaUrl || data.data.whatsappUrl || "",
          fileName: file.name,
          fileType: data.data.fileType || file.type,
        })

        if (typeof window !== "undefined" && window.Swal) {
          window.Swal.fire({
            icon: "success",
            title: "Media Uploaded Successfully!",
            text: `Media ID: ${data.data.mediaId}`,
            confirmButtonColor: "#10b981",
          })
        }
      } else {
        throw new Error(data.error || "Upload failed")
      }
    } catch (error) {
      console.error("Media upload error:", error)
      if (typeof window !== "undefined" && window.Swal) {
        window.Swal.fire({
          icon: "error",
          title: "Upload Failed",
          text: `Error uploading media: ${error.message}`,
          confirmButtonColor: "#ef4444",
        })
      }
    }
  }

  const sendCampaign = async () => {
    if (isSending || isCreating || campaignSent) {
      return
    }

    const mediaType = getMediaType()
    const headerIsMedia = ["image", "video", "document"].includes(mediaType || "")

    if (headerIsMedia && !uploadedMedia) {
      if (typeof window !== "undefined" && window.Swal) {
        await window.Swal.fire({
          icon: "warning",
          title: "Media required",
          text: "This template requires a media header (image/video/document). Please upload the file before sending.",
          confirmButtonColor: "#10b981",
        })
      }
      return
    }

    // 🌍 Country Permission Pre-Validation Check
    let numbersToCheck = []
    if (typeof phoneNumbers === "string" && phoneNumbers.trim()) {
      numbersToCheck = phoneNumbers.split("\n").filter((n) => n && n.trim().length > 0)
    }
    if (numbersToCheck.length > 0) {
      const canProceedCountry = await checkCampaignCountryPermissions(numbersToCheck, fallbackUser, Swal)
      if (!canProceedCountry) {
        return
      }
    }

    if (typeof window !== "undefined" && window.Swal) {
      const result = await window.Swal.fire({
        title: "Send Campaign?",
        text: `Are you sure you want to send this campaign to ${recipientCount} recipients?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#10b981",
        cancelButtonColor: "#6b7280",
        confirmButtonText: "Yes, Send Campaign!",
        cancelButtonText: "Cancel",
      })

      if (!result.isConfirmed) {
        return
      }
    }

    try {
      setIsSending(true)
      setCampaignSent(false)
      let targetCampaignId = campaignId

      if (!targetCampaignId || targetCampaignId === "null") {
        setIsCreating(true)
        const created = await createCampaign()
        setIsCreating(false)
        if (!created) {
          setIsSending(false)
          return
        }
        targetCampaignId = created
      }

      const numbersUploaded = await uploadPhoneNumbers(targetCampaignId)
      if (!numbersUploaded) {
        setIsSending(false)
        if (typeof window !== "undefined" && window.Swal) {
          window.Swal.fire({
            icon: "error",
            title: "Failed to upload phone numbers",
            text: "Please check your phone numbers and try again.",
            confirmButtonColor: "#ef4444",
          })
        }
        return
      }

      const sendResponse = await fetch(`${API_BASE}/api/campaigns/${targetCampaignId}/send`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          wabaAccountId: selectedWabaAccount?.id,
          phoneNumberId: selectedPhoneNumber?.id,
          sendVia: "panel",
          operator: user?.operator || "cloud",
          inputMethod: inputMethod,
          useExcelData: inputMethod === "excel",
          mediaId: uploadedMedia?.mediaId || null,
          mediaUrl: uploadedMedia?.whatsappUrl || null,
          headerContent:
            getMediaType()?.toLowerCase() === "text" && uploadedMedia ? String(uploadedMedia) : undefined,
        }),
      })

      const sendData = await sendResponse.json()

      if (sendData.success) {
        setUserBalance(sendData.remainingBalance || 0)
        setCampaignSent(true)

        if (typeof window !== "undefined" && window.Swal) {
          window.Swal.fire({
            icon: "success",
            title: "Campaign Sent Successfully!",
            html: `
                            <div class="text-left">
                              <p><strong>Total Recipients:</strong> ${sendData.totalMessages || 0}</p>
                              <p><strong>Campaign ID:</strong> ${targetCampaignId?.slice(-8)}</p>
                              <p><strong>Remaining Balance:</strong> ₹${(sendData.remainingBalance || 0).toFixed(2)}</p>
                            </div>
                          `,
            confirmButtonColor: "#10b981",
          }).then((result) => {
            if (result.isConfirmed) {
              // Clear all fields and refresh page
              clearAllFields()
              // Refresh page to reset all state
              setTimeout(() => {
                window.location.reload()
              }, 500)
            }
          })
        }
      } else {
        throw new Error(sendData.error || "Failed to send campaign")
      }
    } catch (error) {
      console.error("Error sending campaign:", error)
      setCampaignSent(false)

      if (typeof window !== "undefined" && window.Swal) {
        window.Swal.fire({
          icon: "error",
          title: "Campaign Send Failed",
          text: error.message || "An unexpected error occurred while sending the campaign.",
          confirmButtonColor: "#ef4444",
        })
      }
    } finally {
      setIsSending(false)
      setIsCreating(false)
    }
  }

  const uploadPhoneNumbers = async (targetCampaignId = campaignId) => {
    if (!targetCampaignId || targetCampaignId === "null" || !phoneNumbers.trim()) return false

    try {
      const numbers = phoneNumbers.split("\n").filter((num) => num.trim().length > 0)
      const csvContent = "phone\n" + numbers.join("\n")
      const blob = new Blob([csvContent], { type: "text/csv" })
      const file = new File([blob], "phone_numbers.csv", { type: "text/csv" })

      const formData = new FormData()
      formData.append("excel", file)

      const response = await fetch(`${API_BASE}/api/campaigns/${targetCampaignId}/upload-numbers`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: formData,
      })

      const data = await response.json()
      return response.ok && data.success
    } catch (error) {
      console.error("Error uploading phone numbers:", error)
      return false
    }
  }

  const renderTemplatePreview = () => {
    if (!selectedTemplate) {
      return (
        <div
          className="phone-mockup mx-auto"
          style={{
            width: "240px",
            height: "420px",
            backgroundColor: "#000",
            borderRadius: "28px",
            padding: "3px",
            boxShadow: "0 8px 30px rgba(0,0,0,0.3)",
            position: "relative",
          }}
        >
          <div
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "#25D366",
              borderRadius: "25px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "0",
                left: "0",
                right: "0",
                height: "32px",
                background: "linear-gradient(180deg, rgba(0,0,0,0.1) 0%, transparent 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0 16px",
                fontSize: "12px",
                fontWeight: "600",
                color: "white",
              }}
            >
              <span>9:41</span>
              <div style={{ display: "flex", gap: "3px" }}>
                <span>📶</span>
                <span>📶</span>
                <span>🔋</span>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "16px",
              }}
            >
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  backgroundColor: "rgba(255,255,255,0.2)",
                  borderRadius: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "20px",
                  backdropFilter: "blur(10px)",
                }}
              >
                <svg width="50" height="50" viewBox="0 0 24 24" fill="white">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.02-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                </svg>
              </div>
              <h3
                style={{
                  color: "white",
                  marginBottom: "12px",
                  fontSize: "18px",
                  fontWeight: "600",
                  textAlign: "center",
                }}
              >
                Select Template
              </h3>
              <p
                style={{
                  color: "rgba(255,255,255,0.8)",
                  fontSize: "12px",
                  margin: 0,
                  textAlign: "center",
                  maxWidth: "160px",
                  lineHeight: "1.4",
                }}
              >
                Choose a template to see the WhatsApp preview
              </p>
            </div>

            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "50%",
                transform: "translateX(-50%)",
                width: "100px",
                height: "4px",
                backgroundColor: "rgba(255,255,255,0.3)",
                borderRadius: "2px",
              }}
            ></div>
          </div>
        </div>
      )
    }

    const analysis = selectedTemplate.parameterAnalysis || {}
    let headerComponent = null
    let bodyComponent = null
    let bodyText = ""

    if (selectedTemplate.components) {
      headerComponent = selectedTemplate.components.find((c) => c.type === "HEADER")
      bodyComponent = selectedTemplate.components.find((c) => c.type === "BODY")

      if (bodyComponent && bodyComponent.text) {
        bodyText = bodyComponent.text

        if (analysis.bodyParameterCount > 0) {
          for (let i = 0; i < analysis.bodyParameterCount; i++) {
            const value = manualVariables[i] || ""
            const placeholder = `{{${i + 1}}}`

            if (value) {
              bodyText = bodyText.replace(placeholder, value)
            } else {
              bodyText = bodyText.replace(placeholder, `[Variable ${i + 1}]`)
            }
          }
        }
      }
    }

    return (
      <div
        className="phone-mockup mx-auto"
        style={{
          width: "240px",
          height: "420px",
          backgroundColor: "#000",
          borderRadius: "28px",
          padding: "3px",
          boxShadow: "0 8px 30px rgba(0,0,0,0.3)",
          position: "relative",
        }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            backgroundColor: "#e5ddd5",
            borderRadius: "25px",
            display: "flex",
            flexDirection: "column",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Header bar */}
          <div
            style={{
              position: "absolute",
              top: "0",
              left: "0",
              right: "0",
              height: "32px",
              backgroundColor: "#075e54",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 16px",
              fontSize: "12px",
              fontWeight: "600",
              color: "white",
              zIndex: 10,
            }}
          >
            <span>9:41</span>
            <div style={{ display: "flex", gap: "3px" }}>
              <span>📶</span>
              <span>📶</span>
              <span>🔋</span>
            </div>
          </div>

          {/* Chat header */}
          <div
            style={{
              backgroundColor: "#075e54",
              padding: "6px 12px",
              marginTop: "32px",
              borderBottom: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <div className="flex items-center">
              <div
                className="bg-yellow-400 rounded-full mr-2 flex items-center justify-center"
                style={{ width: "28px", height: "28px" }}
              >
                <strong style={{ color: "#000", fontSize: "12px" }}>C</strong>
              </div>
              <div>
                <div style={{ fontSize: "14px", fontWeight: "bold", color: "white" }}>
                  {fallbackUser?.username || "Customer"}
                </div>
                <div style={{ fontSize: "10px", color: "rgba(255,255,255,0.8)" }}>online</div>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, padding: "12px", overflowY: "auto" }}>
            <div
              style={{
                backgroundColor: "#dcf8c6",
                borderRadius: "6px",
                padding: "6px 10px",
                marginBottom: "6px",
                maxWidth: "85%",
                marginLeft: "auto",
                boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                position: "relative",
              }}
            >
              <div className="text-xs text-gray-600 mb-1" style={{ fontSize: "9px", color: "#666" }}>
                {selectedTemplate.name}
              </div>

              {/* ✅ Render media if HEADER has image/video/doc */}
              {headerComponent && headerComponent.format === "IMAGE" && (
                <img
                  src={
                    uploadedMedia?.mediaUrl ||
                    uploadedMedia?.whatsappUrl ||
                    (headerComponent.example?.header_handle?.[0]?.startsWith("http")
                      ? headerComponent.example.header_handle[0]
                      : "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=500&auto=format&fit=crop&q=60")
                  }
                  alt="template header"
                  style={{
                    width: "100%",
                    borderRadius: "6px",
                    marginBottom: "8px",
                    maxHeight: "150px",
                    objectFit: "cover",
                  }}
                />
              )}
              {headerComponent && headerComponent.format === "VIDEO" && (
                <div>
                  {(uploadedMedia?.mediaUrl || uploadedMedia?.whatsappUrl || headerComponent.example?.header_handle?.[0]?.startsWith("http")) ? (
                    <video
                      src={uploadedMedia?.mediaUrl || uploadedMedia?.whatsappUrl || headerComponent.example.header_handle[0]}
                      controls
                      style={{
                        width: "100%",
                        borderRadius: "6px",
                        marginBottom: "8px",
                        maxHeight: "150px",
                        backgroundColor: "#000",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        backgroundColor: "#000",
                        color: "#fff",
                        padding: "8px",
                        borderRadius: "6px",
                        marginBottom: "8px",
                        textAlign: "center",
                        fontSize: "10px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100px",
                      }}
                    >
                      <span className="text-xl mb-1">🎥</span>
                      <span>Video Template Header</span>
                      <span className="text-[8px] text-gray-400 mt-1">(Upload a video to preview)</span>
                    </div>
                  )}
                </div>
              )}
              {headerComponent && headerComponent.format === "DOCUMENT" && (
                <div
                  style={{
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #dee2e6",
                    color: "#333",
                    padding: "8px 12px",
                    borderRadius: "6px",
                    marginBottom: "8px",
                    fontSize: "10px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span className="text-xl">📄</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: "600", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                      {uploadedMedia?.fileName || "document.pdf"}
                    </div>
                    <div style={{ fontSize: "8px", color: "#6c757d" }}>
                      {uploadedMedia ? "Uploaded Document" : "PDF Template Header"}
                    </div>
                  </div>
                </div>
              )}

              {/* Body text */}
              {bodyText && <div style={{ fontSize: "12px", color: "#000", lineHeight: "1.4" }}>{bodyText}</div>}

              <div
                style={{
                  textAlign: "right",
                  fontSize: "9px",
                  color: "#666",
                  marginTop: "3px",
                }}
              >
                10:51 AM ✓✓
              </div>

              <div
                style={{
                  position: "absolute",
                  right: "-5px",
                  bottom: "6px",
                  width: "0",
                  height: "0",
                  borderLeft: "5px solid #dcf8c6",
                  borderTop: "5px solid transparent",
                  borderBottom: "5px solid transparent",
                }}
              ></div>
            </div>
          </div>

          {/* Input box */}
          <div
            style={{
              backgroundColor: "#f0f0f0",
              padding: "6px 12px",
              borderTop: "1px solid #ddd",
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "16px",
                padding: "6px 12px",
                fontSize: "12px",
                color: "#999",
              }}
            >
              Type a message...
            </div>
          </div>
        </div>
      </div>
    )
  }

  const canSendCampaign = () => {
    const checks = {
      selectedTemplate: !!selectedTemplate,
      selectedPhoneNumber: fallbackUser?.operator === "cloud" ? !!selectedPhoneNumber : true, // Only required for cloud
      campaignName: campaignName.trim().length > 0,
      recipientCount: recipientCount > 0,
      canProceed: canProceed,
      notSending: !isSending,
      notCreating: !isCreating
    }

    console.log("Can send campaign checks:", checks)

    return (
      selectedTemplate &&
      (fallbackUser?.operator === "cloud" ? selectedPhoneNumber : true) && // Phone number only required for cloud
      campaignName.trim() &&
      recipientCount > 0 &&
      canProceed &&
      !isSending &&
      !isCreating
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Single Campaign</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Create and send campaign in a single page</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Left Side - All Campaign Steps */}
          <div className="md:col-span-8 space-y-6 col-md-8">
            {/* Step 1: Template Selection */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">1. Select Template</h2>

              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={templateSearchQuery}
                  onChange={(e) => setTemplateSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Debug templates */}
              <div className="mb-2 p-2 bg-yellow-100 dark:bg-yellow-900/20 rounded text-xs">
                <div>Templates count: {templates.length}</div>
                <div>Filtered templates count: {filteredTemplates.length}</div>
                <div>Loading: {loading ? 'YES' : 'NO'}</div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-60 overflow-y-auto">
                {filteredTemplates.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => setSelectedTemplate(template)}
                    className={`p-3 border rounded-lg cursor-pointer transition-all ${selectedTemplate?.id === template.id
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                        : "border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500"
                      }`}
                  >
                    <div className="font-medium text-sm text-gray-900 dark:text-white">{template.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{template.category}</div>
                  </div>
                ))}
              </div>

              {selectedTemplate && (
                <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <div className="text-sm font-medium text-green-800 dark:text-green-200">
                    Selected: {selectedTemplate.name}
                  </div>
                </div>
              )}
            </div>

            {/* Step 2: Campaign Name & Media */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">2. Campaign Details</h2>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Campaign Name
                  {isCreating && <span className="ml-2 text-blue-600 text-xs">(Creating campaign...)</span>}
                  {campaignCreated && <span className="ml-2 text-green-600 text-xs">(Campaign ready)</span>}
                </label>
                <input
                  type="text"
                  placeholder="Enter campaign name"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Phone Number Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select Phone Number
                </label>

                {/* Debug phone numbers */}


                {fallbackUser?.operator === "cloud" && selectedWabaAccount && selectedWabaAccount.phoneNumbers && selectedWabaAccount.phoneNumbers.length > 0 ? (
                  <select
                    value={selectedPhoneNumber?.id || ""}
                    onChange={(e) => {
                      const phone = selectedWabaAccount.phoneNumbers.find(p => p.id === e.target.value)
                      setSelectedPhoneNumber(phone)
                      console.log("Manually selected phone:", phone)
                    }}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">phone number</option>
                    {selectedWabaAccount.phoneNumbers.map((phone) => (
                      <option key={phone.id} value={phone.id}>
                        {phone.displayPhoneNumber || phone.phoneNumber} {phone.default && '(Default)'}
                      </option>
                    ))}
                  </select>
                ) : fallbackUser?.operator === "cloud" ? (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    No phone numbers available. Please check your WABA account setup.
                  </div>
                )
                  : (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Phone number select automatically
                    </div>
                  )

                }
              </div>

              {/* Debug info - remove later */}


              {selectedTemplate && getMediaType() && ["image", "video", "document"].includes(getMediaType()) && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Media Upload - {getMediaType()?.toUpperCase()} Required
                  </label>

                  {getMediaType() && (
                    <div className="mb-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-xs text-blue-700 dark:text-blue-300">
                      {getMediaType() === 'image' && '📷 Please upload an image file (JPG, PNG, etc.)'}
                      {getMediaType() === 'video' && '🎥 Please upload a video file (MP4, MOV, etc.)'}
                      {getMediaType() === 'document' && '📄 Please upload a document file (PDF, DOC, DOCX, etc.)'}
                    </div>
                  )}

                  <div className="relative">
                    <input
                      type="file"
                      onChange={handleMediaUpload}
                      accept={
                        getMediaType() === 'image' ? 'image/*' :
                          getMediaType() === 'video' ? 'video/*' :
                            getMediaType() === 'document' ? '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx' :
                              'image/*,video/*,.pdf,.doc,.docx'
                      }
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/20 dark:file:text-blue-300"
                    />
                  </div>

                  {uploadedMedia && (
                    <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-green-800 dark:text-green-200">
                            Media uploaded successfully
                          </div>
                          <div className="text-sm text-green-700 dark:text-green-300 mt-1">
                            {uploadedMedia.fileName}
                          </div>
                          <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                            Type: {uploadedMedia.fileType || 'Unknown'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Step 3: Variables */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">3. Variables</h2>

              {selectedTemplate && selectedTemplate.parameterAnalysis?.bodyParameterCount > 0 ? (
                <div className="space-y-3">
                  {/* Toggle for saved vs manual variables */}
                  <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <label className="flex items-center space-x-3 text-sm">
                      <input
                        type="checkbox"
                        checked={useSavedVariables}
                        onChange={(e) => setUseSavedVariables(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        Use saved variables for this template
                      </span>
                    </label>
                    <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                      Variables are saved in database and persist across sessions
                    </div>
                    {useSavedVariables && (
                      <div className="mt-2 text-xs text-blue-600 dark:text-blue-400">
                        {Object.keys(manualVariables).some(key => manualVariables[key]?.trim())
                          ? "✓ Using saved variables"
                          : "Loading saved variables..."}
                      </div>
                    )}

                  </div>

                  {Array.from({ length: selectedTemplate.parameterAnalysis.bodyParameterCount }, (_, i) => (
                    <div key={i}>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Variable {i + 1}
                        {useSavedVariables && (
                          <span className="ml-2 text-xs text-blue-600">(Database Saved)</span>
                        )}
                      </label>
                      <input
                        type="text"
                        placeholder={`Enter value for variable ${i + 1}`}
                        value={manualVariables[i] || ""}
                        onChange={(e) => {
                          setManualVariables({ ...manualVariables, [i]: e.target.value })
                        }}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Variables are automatically saved to database
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-gray-500 dark:text-gray-400 text-sm">
                  No variables required for this template
                </div>
              )}
            </div>

            {/* Step 4: Recipients */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">4. Recipients</h2>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Phone Numbers (one per line)
                </label>
                <textarea
                  placeholder="Enter phone numbers (one per line)&#10;Example:&#10;919876543210&#10;919876543211"
                  value={phoneNumbers}
                  onChange={(e) => setPhoneNumbers(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div className="text-sm text-gray-600 dark:text-gray-400">
                Recipients: {recipientCount}
              </div>

              {balanceValidation && (
                <div className={`mt-3 p-3 rounded-lg text-sm ${balanceValidation.status === "success"
                    ? "bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-200"
                    : "bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-200"
                  }`}>
                  {balanceValidation.message}
                </div>
              )}
            </div>

            {/* Send Button */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              {/* Debug send campaign conditions */}


              <button
                onClick={sendCampaign}
                disabled={!canSendCampaign()}
                className={`w-full py-3 px-6 rounded-lg font-medium transition-all ${canSendCampaign()
                    ? "bg-blue-600 hover:bg-blue-700 text-white"
                    : "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  }`}
              >
                {isSending || isCreating ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {isCreating ? "Creating Campaign..." : "Sending Campaign..."}
                  </span>
                ) : (
                  "Send Campaign"
                )}
              </button>
            </div>
          </div>

          {/* Right Side - Phone Preview */}
          <div className="md:col-span-4 md:sticky md:top-6 h-fit col-md-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <svg
                  className="w-5 h-5 mr-2 text-green-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M7 2a2 2 0 00-2 2v12a2 2 0 002 2h6a2 2 0 002-2V4a2 2 0 00-2-2H7zM6 4a1 1 0 011-1h6a1 1 0 011 1v12a1 1 0 01-1 1H7a1 1 0 01-1-1V4z"
                    clipRule="evenodd"
                  />
                </svg>
                WhatsApp Preview
              </h3>
              {renderTemplatePreview()}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SingleCampaign
